import * as React from 'react';
import { Calendar } from '@/components/ui/calendar';
import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * `<CalendarRangeStyles />` — drop-in `<style>` element that applies
 * the shared range-picker CSS across every calendar in the app
 * (`Home`, `IndexMinimal`, `GoogleFlightsSearch`, `Search`).
 *
 * Design goals — modeled on Google Flights' calendar:
 * - Range MIDDLE renders as a continuous horizontal band, not a
 *   row of individual translucent pills. Achieved by (a) making
 *   the middle-day button rectangular + full-cell-width and (b)
 *   painting the band background on the CELL (via `:has()`) so it
 *   also shows around the start / end pills for a seamless look.
 * - Range START / END buttons remain as white circular pills sitting
 *   ON TOP of the band — the visual anchors of the range.
 * - No text-decoration anywhere on day contents (guards against a
 *   dashed "cheapest date" underline creeping in from anywhere).
 *
 * We inline the CSS instead of adding it to a global stylesheet so
 * it's colocated with the component that depends on it — dropping
 * `<CalendarRangeStyles />` into a page's calendar block is a
 * one-line change. `:has()` is baseline in all modern browsers we
 * target.
 */
const CALENDAR_RANGE_CSS = [
  // Brand endpoints: vivid royal-blue pills with a soft glow — the visual
  // anchors of the range. (Previously stark white/black, the one element on
  // the page that ignored the dark theme.)
  '.rdp-day_selected { background-color: hsl(228, 62%, 50%) !important; color: white !important; box-shadow: 0 0 16px -4px hsla(228, 80%, 60%, 0.55); }',
  '.rdp-day_selected:hover { background-color: hsl(228, 62%, 56%) !important; color: white !important; }',
  // Middle: transparent (so the cell band shows through), rectangular, and full-width so
  // adjacent middle cells never leave a horizontal gap.
  '.rdp-day_range_middle { background-color: transparent !important; color: rgba(255,255,255,0.92) !important; border-radius: 0 !important; width: 100% !important; max-width: 100% !important; box-shadow: none; }',
  '.rdp-day_range_start { background-color: hsl(228, 62%, 50%) !important; color: white !important; }',
  '.rdp-day_range_end { background-color: hsl(228, 62%, 50%) !important; color: white !important; }',
  // Cell-level band: paint any cell that contains a range-part day, then round the LEFT
  // of the start cell and the RIGHT of the end cell so the band terminates in a pill shape.
  '.rdp-cell:has(.rdp-day_range_start), .rdp-cell:has(.rdp-day_range_middle), .rdp-cell:has(.rdp-day_range_end) { background-color: hsla(228, 70%, 58%, 0.16); }',
  '.rdp-cell:has(.rdp-day_range_start) { border-top-left-radius: 9999px; border-bottom-left-radius: 9999px; }',
  '.rdp-cell:has(.rdp-day_range_end) { border-top-right-radius: 9999px; border-bottom-right-radius: 9999px; }',
  // Kill any inherited text-decoration on day contents (no dashed "cheap" underline).
  '.rdp-day, .rdp-day span { text-decoration: none !important; }',
].join(' ');

export function CalendarRangeStyles() {
  return <style>{CALENDAR_RANGE_CSS}</style>;
}

/**
 * Format a Date to `YYYY-MM-DD` using LOCAL date parts — matches the
 * key format used by `/api/flight-calendar-prices` when the server
 * generates its range from a local-midnight anchor. react-day-picker
 * hands `Day` components a local-time Date object; reading local
 * date parts here keeps the lookup on the same day the user sees.
 */
function toYmdKey(date) {
  if (!(date instanceof Date) || isNaN(date.getTime())) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatPriceCompact(price) {
  const n = Number(price);
  if (!Number.isFinite(n)) return '';
  if (n < 1000) return `$${Math.round(n)}`;
  return `$${(n / 1000).toFixed(1)}k`;
}

/**
 * `<CalendarWithPrices>` extends the base `<Calendar>` with a tiny
 * "$X" hint under each day, matching Google Flights' own date-picker.
 *
 * Layout choices:
 * - The price sits INSIDE the day button rather than absolute-
 *   positioned outside it. The button IS the pill react-day-picker
 *   paints white on selection — putting the price inside means the
 *   selected pill's white background wraps around the price too,
 *   which is what customers expect.
 * - Day-button height (`h-10`) and the price slot are applied
 *   UNCONDITIONALLY. If we only bumped the size when prices were
 *   loaded, the picker would visibly resize every time the customer
 *   navigated to a month whose prices weren't yet cached (fetch in
 *   flight → cells small → prices arrive → cells jump bigger). By
 *   reserving the price slot even when it's empty, dimensions stay
 *   stable across month-to-month navigation and cache misses.
 * - Regular prices inherit the button's own text color via
 *   `text-current` + `opacity-45`, so they read as muted-white on
 *   the dark unselected buttons AND as muted-black on the white
 *   selected pill without any per-modifier CSS gymnastics.
 * - Lowest-price emerald is explicit and stays legible on both
 *   dark and white backgrounds.
 */
export function CalendarWithPrices({
  dayPrices,
  lowestPrice,
  isLoading, // eslint-disable-line no-unused-vars
  className,
  classNames,
  ...calendarProps
}) {
  const priceMap = dayPrices || {};
  const lowestValue = typeof lowestPrice === 'number' && lowestPrice > 0 ? lowestPrice : null;

  const DayContent = React.useCallback(({ date }) => {
    const key = toYmdKey(date);
    const price = key != null ? priceMap[key] : null;
    const isLowest = price != null && lowestValue != null && price === lowestValue;
    return (
      <span className="inline-flex flex-col items-center justify-center leading-none">
        <span>{date.getDate()}</span>
        {/* Price slot ALWAYS renders so day-cell dimensions stay
            stable regardless of whether the current month's prices
            are cached, fetching, or empty. When no price is
            available we still emit a placeholder glyph with
            `invisible` so the reserved height is preserved but the
            character never paints. */}
        <span
          aria-hidden={price == null ? 'true' : undefined}
          className={cn(
            'mt-0.5 text-[9px] leading-none font-normal tabular-nums tracking-tight',
            price == null ? 'invisible' : null,
            isLowest ? 'text-emerald-500' : 'text-current opacity-45'
          )}
        >
          {price != null ? formatPriceCompact(price) : '$0'}
        </span>
      </span>
    );
  }, [priceMap, lowestValue]);

  return (
    <Calendar
      {...calendarProps}
      className={className}
      classNames={{
        // Replaces the base Calendar's `day` classes but re-includes
        // `buttonVariants({ variant: 'ghost' })` so the button keeps
        // its ghost styling (rounded-full pill, hover states, focus
        // ring, transitions). `h-10 w-full md:w-10` gives the button
        // enough vertical room for date + price without disturbing
        // the horizontal density.
        day: cn(
          buttonVariants({ variant: 'ghost' }),
          'h-10 w-full md:w-10 p-0 font-normal aria-selected:opacity-100'
        ),
        ...classNames,
      }}
      components={{ DayContent }}
    />
  );
}
